import React, { useState, useEffect } from "react";
import { getInfraCost, updateInfraCost } from "services/configuration";
import Toast from "components/common/Toast";
import UpdateCostModal from "components/common/modals/UpdateCostModal";

const UpdateInfraCost: React.FC = () => {
  const [infraCost, setInfraCost] = useState<number>(0);
  const [newInfraCost, setNewInfraCost] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [updating, setUpdating] = useState<boolean>(false);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [costToUpdate, setCostToUpdate] = useState<number>(0);
  const [isButtonEnabled, setIsButtonEnabled] = useState<boolean>(false);

  // Fetch current infra cost on component mount
  useEffect(() => {
    fetchInfraCost();
  }, []);

  const fetchInfraCost = async () => {
    try {
      setLoading(true);
      const response = await getInfraCost();
      if (response?.data?.ipvijil_infra_cost) {
        const cost = response.data.ipvijil_infra_cost;
        setInfraCost(cost);
        setNewInfraCost(cost.toString());
        // Initially button should be disabled as the value is the same
        setIsButtonEnabled(false);
      }
    } catch (error) {
      console.error("Error fetching infrastructure cost:", error);
      Toast.error("Error", "Failed to fetch infrastructure cost");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateInfraCost = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate input
    const costValue = parseFloat(newInfraCost);
    if (isNaN(costValue) || costValue <= 0) {
      Toast.error("Error", "Please enter a valid positive number");
      return;
    }

    // Set the cost to update and show confirmation modal
    setCostToUpdate(costValue);
    setShowConfirmModal(true);
  };

  const confirmUpdateCost = async () => {
    try {
      setUpdating(true);
      await updateInfraCost(costToUpdate);

      // Update local state
      setInfraCost(costToUpdate);

      Toast.success("Success", "Infrastructure cost updated successfully");
    } catch (error) {
      console.error("Error updating infrastructure cost:", error);
      Toast.error("Error", "Failed to update infrastructure cost");
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="w-full">
      <div className="mb-6">
        <h3 className="text-lg font-interSemiBold text-[#101828] mb-2">
          Infrastructure Cost Settings
        </h3>
        <p className="text-sm text-[#475467] mb-6">
          This value is used to calculate infrastructure costs for all teams.
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <div className="border border-[#EAECF0] rounded-lg p-6 bg-white">
          <div className="mb-6">
            <div className="flex items-center mb-4">
              <span className="text-[#101828] font-interMedium">
                Current Infrastructure Cost:
              </span>
              <span className="ml-2 text-[#1E0AF5] font-interSemiBold">
                ${infraCost.toFixed(2)}
              </span>
            </div>
          </div>

          <form onSubmit={handleUpdateInfraCost}>
            <div className="mb-4">
              <label
                htmlFor="newInfraCost"
                className="block text-sm font-interMedium text-[#344054] mb-1"
              >
                New Infrastructure Cost ($)
              </label>
              <input
                type="number"
                id="newInfraCost"
                value={newInfraCost}
                onChange={(e) => {
                  const value = e.target.value;
                  setNewInfraCost(value);

                  // Enable button only if the new value is valid and different from current
                  const numValue = parseFloat(value);
                  setIsButtonEnabled(
                    !isNaN(numValue) && numValue > 0 && numValue !== infraCost
                  );
                }}
                className="w-full md:w-64 px-3 py-2 border border-[#D0D5DD] rounded-md focus:outline-none focus:ring-2 focus:ring-[#1E0AF5] focus:border-transparent"
                placeholder="Enter new cost"
                step="0.01"
                min="0"
                required
              />
            </div>
            <button
              type="submit"
              disabled={updating || !isButtonEnabled}
              className={`submit-btn-blue !px-[15px] !text-sm ${
                updating || !isButtonEnabled
                  ? "button-disabled"
                  : "bg-[#1751D0] hover:bg-[#113B98]"
              }`}
            >
              {updating ? "Updating..." : "Update Cost"}
            </button>
          </form>
        </div>
      )}

      {/* Confirmation Modal */}
      <UpdateCostModal
        isShow={showConfirmModal}
        setIsShow={setShowConfirmModal}
        currentCost={infraCost}
        newCost={costToUpdate}
        onConfirm={confirmUpdateCost}
      />
    </div>
  );
};

export default UpdateInfraCost;
